import { CopyOutlined } from '@ant-design/icons';
import { ConnectError } from '@bufbuild/connect';
import {
  Alert,
  Button,
  Input,
  Modal,
  notification,
  QRCode,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { MFAStatus } from 'protocol/api/auth/dto_auth_pb';

import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { authModel } from '@/entities/auth';
import { userModel } from '@/entities/user';

import {
  enforceBlockMessages,
  MFAPermissions,
  userSecurityMessages as messages,
} from '../../config';
import { canManageTargetMFA } from '../../lib';

import styles from './userSecurity.module.scss';

const { Title, Text, Paragraph } = Typography;

type UserSecurityProps = {
  userID: string;
};

type EnrollSecret = {
  Secret: string;
  OtpauthURI: string;
};

type StatusRow = {
  key: string;
  property: string;
  value: ReactNode;
};

export const UserSecurity: FC<UserSecurityProps> = (props) => {
  const { userID } = props;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { formatMessage, formatDate } = useIntl();

  const adminUser = useAppSelector(userModel.selectors.selectAdminUser);
  const canViewMFA = useAppSelector(authModel.selectors.selectCanViewMFA);
  const canEnforceMFA = useAppSelector(authModel.selectors.selectCanEnforceMFA);
  const canSetupMFA = useAppSelector(authModel.selectors.selectCanSetupMFA);
  const canEditAdminMFA = useAppSelector(
    authModel.selectors.selectCanEditAdminMFA,
  );

  const [status, setStatus] = useState<MFAStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isEnforceSaving, setIsEnforceSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [enrollSecret, setEnrollSecret] = useState<EnrollSecret | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [signInRequired, setSignInRequired] = useState(false);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);

    try {
      const { Status } = await dispatch(
        authModel.thunks.getAdminMFAStatus({ UserID: userID }),
      ).unwrap();

      setStatus(Status ?? null);
    } catch {
      setLoadError(true);
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userID]);

  useEffect(() => {
    setEnrollSecret(null);
    setEnrollCode('');
    setRecoveryCodes([]);

    fetchStatus();
  }, [fetchStatus]);

  if (!canViewMFA) {
    return null;
  }

  const mfaPermissions: MFAPermissions = {
    canEnforce: canEnforceMFA,
    canSetup: canSetupMFA,
    canEditAdmin: canEditAdminMFA,
  };

  const { canEnforce, canRemove, enforceBlock } = canManageTargetMFA(
    status,
    mfaPermissions,
  );

  // The enroll RPCs act on the caller's own session, so setup is offered
  // only on the signed-in admin's own page
  const isSelf = userID === adminUser?.ID;
  const canSetupSelf =
    isSelf &&
    canSetupMFA &&
    Boolean(status) &&
    !status?.Enabled &&
    !enrollSecret;

  const handleEnforceToggle = async (enforced: boolean) => {
    setIsEnforceSaving(true);

    try {
      const { Status } = await dispatch(
        authModel.thunks.setAdminMFAEnforcement({
          UserID: userID,
          Enforced: enforced,
        }),
      ).unwrap();

      setStatus(Status ?? null);
      notification.success({
        message: formatMessage(messages.enforceSuccess),
      });
    } catch (error) {
      notification.error({ message: ConnectError.from(error).message });
    } finally {
      setIsEnforceSaving(false);
    }
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: formatMessage(messages.removeConfirmTitle),
      content: formatMessage(messages.removeConfirmContent),
      okText: formatMessage(messages.remove),
      okButtonProps: { danger: true },
      cancelText: formatMessage({
        id: 'global.cancel',
        defaultMessage: 'Cancel',
      }),
      onOk: async () => {
        setIsRemoving(true);

        try {
          const { Status } = await dispatch(
            authModel.thunks.removeAdminMFA({ UserID: userID }),
          ).unwrap();

          setStatus(Status ?? null);
          notification.success({
            message: formatMessage(messages.removeSuccess),
          });
        } catch (error) {
          notification.error({ message: ConnectError.from(error).message });
        } finally {
          setIsRemoving(false);
        }
      },
    });
  };

  const handleBeginSetup = async () => {
    try {
      const { Secret, OtpauthURI } = await dispatch(
        authModel.thunks.beginMFAEnroll({}),
      ).unwrap();

      setEnrollSecret({ Secret, OtpauthURI });
      setEnrollCode('');
    } catch {
      notification.error({ message: formatMessage(messages.setupError) });
    }
  };

  const handleCancelSetup = () => {
    setEnrollSecret(null);
    setEnrollCode('');
  };

  const handleConfirmSetup = async () => {
    setIsConfirming(true);

    try {
      const { RecoveryCodes, SignInRequired } = await dispatch(
        authModel.thunks.confirmMFAEnroll({ Code: enrollCode.trim() }),
      ).unwrap();

      setRecoveryCodes(RecoveryCodes);
      setSignInRequired(SignInRequired);
      setEnrollSecret(null);
      setEnrollCode('');

      await fetchStatus();
    } catch {
      notification.error({ message: formatMessage(messages.confirmError) });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCopyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      notification.success({
        message: formatMessage(messages.copySuccess),
      });
    } catch {
      notification.error({ message: formatMessage(messages.copyError) });
    }
  };

  const handleRecoveryDone = async () => {
    if (signInRequired) {
      await dispatch(authModel.thunks.logout({})).unwrap();
      navigate(PATHS.signIn);
      return;
    }

    setRecoveryCodes([]);
  };

  const statusRows: StatusRow[] = status
    ? [
        {
          key: 'status',
          property: formatMessage(messages.statusLabel),
          value: (
            <Tag color={status.Enabled ? 'green' : 'default'}>
              {formatMessage(
                status.Enabled
                  ? messages.statusEnabled
                  : messages.statusDisabled,
              )}
            </Tag>
          ),
        },
        {
          key: 'setupAt',
          property: formatMessage(messages.setupAtLabel),
          value: status.SetupAt
            ? formatDate(status.SetupAt.toDate(), {
                year: 'numeric',
                month: 'long',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—',
        },
        {
          key: 'recoveryCodesLeft',
          property: formatMessage(messages.recoveryCodesLeftLabel),
          value: status.Enabled ? status.RecoveryCodesLeft : '—',
        },
      ]
    : [];

  const statusColumns = [
    {
      title: formatMessage(messages.colProperty),
      dataIndex: 'property',
      key: 'property',
      width: 260,
    },
    {
      title: formatMessage(messages.colValue),
      dataIndex: 'value',
      key: 'value',
    },
  ];

  return (
    <div className={styles.container}>
      <div>
        <Title level={4}>{formatMessage(messages.statusTitle)}</Title>
        <Paragraph type="secondary">
          {formatMessage(messages.statusIntro)}
        </Paragraph>
      </div>

      {loadError && (
        <Alert
          type="error"
          showIcon
          message={formatMessage(messages.loadError)}
          action={
            <Button size="small" onClick={fetchStatus}>
              {formatMessage(messages.retry)}
            </Button>
          }
        />
      )}

      <Table<StatusRow>
        rowKey="key"
        size="small"
        loading={isLoading}
        columns={statusColumns}
        dataSource={statusRows}
        pagination={false}
      />

      {status && (
        <div className={styles.actions}>
          <Space size={16} wrap>
            <Space size={8}>
              <Tooltip
                title={
                  enforceBlock &&
                  formatMessage(enforceBlockMessages[enforceBlock])
                }
              >
                <Switch
                  checked={status.Enforced}
                  loading={isEnforceSaving}
                  disabled={!canEnforce}
                  onChange={handleEnforceToggle}
                />
              </Tooltip>
              <Text>{formatMessage(messages.enforceLabel)}</Text>
            </Space>

            {canRemove && (
              <Button danger loading={isRemoving} onClick={handleRemoveClick}>
                {formatMessage(messages.remove)}
              </Button>
            )}

            {canSetupSelf && (
              <Button type="primary" onClick={handleBeginSetup}>
                {formatMessage(messages.setup)}
              </Button>
            )}
          </Space>
        </div>
      )}

      {enrollSecret && (
        <div className={styles.enroll}>
          <Title level={5} style={{ marginBottom: 8 }}>
            {formatMessage(messages.setupTitle)}
          </Title>

          <Paragraph type="secondary">
            {formatMessage(messages.setupIntro)}
          </Paragraph>

          <div className={styles.qr}>
            <QRCode value={enrollSecret.OtpauthURI} size={180} />
          </div>

          <div className={styles.secretRow}>
            <Text type="secondary">{formatMessage(messages.secretLabel)}:</Text>
            <Text code copyable>
              {enrollSecret.Secret}
            </Text>
          </div>

          <Space size={8}>
            <Input
              value={enrollCode}
              onChange={(event) => setEnrollCode(event.target.value)}
              placeholder={formatMessage(messages.codePlaceholder)}
              maxLength={6}
              style={{ width: 160 }}
              onPressEnter={handleConfirmSetup}
            />
            <Button
              type="primary"
              loading={isConfirming}
              disabled={!enrollCode.trim()}
              onClick={handleConfirmSetup}
            >
              {formatMessage(messages.confirm)}
            </Button>
            <Button onClick={handleCancelSetup}>
              {formatMessage({
                id: 'global.cancel',
                defaultMessage: 'Cancel',
              })}
            </Button>
          </Space>
        </div>
      )}

      {recoveryCodes.length > 0 && (
        <div className={styles.recovery}>
          <Title level={5} style={{ marginBottom: 8 }}>
            {formatMessage(messages.recoveryCodesTitle)}
          </Title>

          <Alert
            type="warning"
            showIcon
            message={formatMessage(messages.recoveryCodesWarning)}
          />

          <ul className={styles.codes}>
            {recoveryCodes.map((code) => (
              <li key={code}>
                <Text code>{code}</Text>
              </li>
            ))}
          </ul>

          {signInRequired && (
            <Text>{formatMessage(messages.signInRequired)}</Text>
          )}

          <Space size={8}>
            <Button
              icon={<CopyOutlined rev={undefined} />}
              onClick={handleCopyRecoveryCodes}
            >
              {formatMessage(messages.copyAll)}
            </Button>
            <Button type="primary" onClick={handleRecoveryDone}>
              {formatMessage(
                signInRequired ? messages.signInAgain : messages.done,
              )}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};
